// customer master (CRM) domain — the single source of truth for buyer identity. orders link
// to a customer, while each committed order revision keeps its own immutable buyer snapshot
// (so editing a customer never rewrites history). normalization helpers here are shared by the
// UI (search/display) and the worker (duplicate detection) so the two never drift.
export const CUSTOMER_TYPES = ['individual', 'company'];
export const CUSTOMER_STATUSES = ['active', 'archived', 'merged'];
// ── normalization (search + duplicate detection) ─────────────────────────────
/** strip diacritics/accents (NFD → drop combining marks) — "Müller" → "Muller". */
export function stripAccents(s) {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
/** lowercase, de-accent, collapse whitespace, trim. General-purpose text key. */
export function normalizeText(s) {
    return stripAccents(String(s ?? ''))
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
export function normalizeEmail(s) {
    return String(s ?? '').toLowerCase().replace(/\s+/g, '').trim();
}
/** digits only; drop a leading 00/+ and the Greek (30) country code so +30 694… ≡ 694…. */
export function normalizePhone(s) {
    let d = String(s ?? '').replace(/[^\d+]/g, '');
    d = d.replace(/^\+/, '').replace(/^00/, '');
    if (d.length === 12 && d.startsWith('30'))
        d = d.slice(2); // GR country code
    return d;
}
/** uppercase, remove separators — "EL 123.456-789" → "EL123456789". */
export function normalizeVat(s) {
    return String(s ?? '').toUpperCase().replace(/[\s.\-_/]/g, '').trim();
}
const COMPANY_SUFFIXES = /\b(s\.?a\.?|ltd\.?|limited|inc\.?|llc|co\.?|corp\.?|a\.?e\.?|e\.?p\.?e\.?|o\.?e\.?|gmbh|s\.?r\.?l\.?|b\.?v\.?|n\.?v\.?)\b/gi;
/** company key: de-accent/lowercase, drop punctuation + common legal suffixes so
 *  "Technohull S.A." ≡ "TECHNOHULL SA". dots are removed (not spaced) first so "s.a." → "sa". */
export function normalizeCompany(s) {
    return normalizeText(s)
        .replace(/[.,]/g, '')
        .replace(COMPANY_SUFFIXES, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** person key: "first last" normalized (order-independent tokens kept as given). */
export function normalizePerson(first, last) {
    return normalizeText(`${first ?? ''} ${last ?? ''}`);
}
/** best display name for a customer (company for companies, person otherwise, then fallbacks). */
export function customerDisplayName(c) {
    const company = (c.companyName ?? '').trim();
    const person = `${c.firstName ?? ''} ${c.lastName ?? ''}`.replace(/\s+/g, ' ').trim();
    const pick = c.customerType === 'company' ? company || person : person || company;
    return pick || (c.email ?? '').trim() || (c.id ? `Customer #${c.id}` : 'Unnamed customer');
}
//# sourceMappingURL=customer.js.map